/**
 * @file machine_app_view.js
 * @author MachineApps Team
 *
 * @description Creates a view for the machine app type provided. This is used by 'gui_pane_machineapp.js' in assembler,
 * and by the 'MachineApp' page on the pendant. The pendant version is temporary.
 */

import $ from 'jQuery';
import machine_app_implementation_loader from '../models/machine_app_implementation_loader';
import machine_app_state from '../models/machine_app_state';
import { developerTools } from '../machine_app_debugger';
import { gMachineAppViews } from '../machine_app_global_variables';
import { greenCheckbox } from '../components/machine_app_green_checkbox';
import { canOpenView, getPreviousView, isStepValid } from '../machine_app_view_validation';
import { machineAppItemView } from './machine_app_item_view';

/**
 * @typedef {'machineParameters' | 'globalAsset' | 'configuration' | 'application'} MachineAppViewString
 */

const gOrderedHeaderItems = [
  gMachineAppViews.machineParameters,
  gMachineAppViews.globalAsset,
  gMachineAppViews.configuration,
  gMachineAppViews.application
];

/**
 * Main entry point into the MachineApps render. This is responsible for opening up views in the right pane
 * depending on what is chosen in the left pane.
 *
 * @param {JQuery} pMain Element to append the MachineApp view to
 * @param {String} pMachineAppType Type of MachineApp that we want to view
 * @param {Library} pMachineLogicLibrary MachineLogicLibrary
 *
 * @returns {Object} With a function to re-render the view and to close the view.
 */
export function machineAppView(pLeftPane, pRightPane, pMachineAppType, pMachineLogicLibrary, pOnBack, pStartingView) {
  const machineAppLibrary = machine_app_state.getLibraryManager().getLibraryByType(pMachineAppType);
  const lTreeViewList = $('<div>').addClass('v_layout ml_scrollable machineapp_left_pane_tree');
  const lMachineAppDefinitionName = machine_app_state.getImplementationManager().getMachineAppDefinitionByType(pMachineAppType).getName();
  const lGlobalAssetCreatorMap = machine_app_implementation_loader.instance.getGlobalAssetCreatorMap(pMachineAppType);
  const viewsInLeftPane = gOrderedHeaderItems.filter(view => {
    if (view === gMachineAppViews.globalAsset && !lGlobalAssetCreatorMap) {
      return false;
    }
    return true;
  });

  /**
   * Function that opens view
   *
   * @param {MachineAppViewString} pViewType
   */
  function lOpenView(pViewType) {
    //check if we can open it, if not go up the step list
    if (!canOpenView(pViewType, viewsInLeftPane, machineAppLibrary)) {
      lOpenView(getPreviousView(pViewType, viewsInLeftPane));
      return;
    }

    // -- Set the view and re-render
    machine_app_state.getInterface().updateMachineAppView(pMachineAppType, pViewType);

    $(pRightPane).empty().append(machineAppItemView({
      machineAppLibrary:        machineAppLibrary,
      view:                     pViewType,
      updateLeftPane:           updateLeftPane,
      machineAppDefinitionName: lMachineAppDefinitionName,
      globalAssetCreatorMap:    lGlobalAssetCreatorMap,
      openView:                 lOpenView,
      orderedViews:             gOrderedHeaderItems
    }));

    updateLeftPane(pViewType);
  }

  /**
   * Updates the headers in the left pane.
   * If we pass a newView to the function, we change the selected style.
   *
   * @param {MachineAppViewString} [newView] The name of the new view, if any
   */
  function updateLeftPane(newView = undefined) {
    lItemHeaders.forEach(({widget, controls, view}, index) => {
      controls.updateIcon(getHeaderIconElement(view, viewsInLeftPane, machineAppLibrary));
      controls.updateTitle(getHeaderTitle(view, pMachineAppType, machineAppLibrary));

      if (newView) {
        widget.removeClass('selected');

        if (view === newView) {
          widget.addClass('selected');
        }
      }

      if (index + 1 >= lItemHeaders.length) return;

      const nextHeader = lItemHeaders[index + 1];
      if (!isStepValid(view, viewsInLeftPane, machineAppLibrary)) {
        nextHeader.widget.addClass('disabled');
      }
      else {
        nextHeader.widget.removeClass('disabled');
      }
    });
  }

  // Define actual UI elements
  /////////////////////////////////////////
  const lItemHeaders = getItemHeaders(viewsInLeftPane, pMachineAppType, machineAppLibrary, lOpenView);
  const lLeftPaneContainer = $('<div>').addClass('bordered spaced machineapp_left_pane').appendTo(pLeftPane).on('click', function (pEvent) {
    if (pEvent.ctrlKey && pEvent.shiftKey) {
      pEvent.preventDefault();
      pEvent.stopPropagation();
      developerTools(function () {
        lOpenView(viewsInLeftPane[0]);
      }, pMachineAppType);
    }
  });
  const lMachineAppDefinitionNameElement = $('<div>').addClass('machineapp_editor_left_pane_header_title').append(
    $('<i>').addClass('icon-machine-app'),
    $('<h5>').text(`${lMachineAppDefinitionName} Setup`)
  );
  const lBackButton = $('<button>')
    .append(
      $('<i>')
        .addClass('icon-back')
        .addClass('machineapp_editor_left_pane_header_back_btn_icon'),
      $('<span>')
        .text('Open or Create App')
    )
    .on('click', pOnBack)
    .addClass('machineapp_editor_left_pane_header_back_btn')
    .prop('disabled', !!pOnBack);// //HACK for PATH-FOLLOWING-VENTION-IO. disabling on back if onBack doesn't exist

  // View structure
  /////////////////////////////////////////
  // Left pane
  lLeftPaneContainer.append(
    // lBackButton,
    lMachineAppDefinitionNameElement,
    lTreeViewList.empty().append(
      ...lItemHeaders.map(header => {
        return header.widget;
      })
    )
  );

  // Right pane
  const startingView = pStartingView || viewsInLeftPane[0];
  lOpenView(startingView);
}

/////////////////////////////////////////////////
// Defining Header Util functions
/////////////////////////////////////////////////

/**
 * @typedef {Object} ControlObject
 * @property {typeof getHeaderIconElement} updateIcon
 * @property {typeof getHeaderTitle} updateTitle
 */
/**

 /**
 * @typedef {Object} LeftPaneItem
 * @property {JQuery} widget
 * @property {ControlObject} controls The control functions for changing the icon or title of a header
 * @property {MachineAppViewString} view The name of the view associated with the header
 */

/**
 * @callback OpenView
 * @param {MachineAppViewString}
 */

/**
 * Builds UI elements ready for the left pane
 *
 * @param {Number} pStepNumber
 * @param {JQuery} pIconElement
 * @param {String} pTitle
 * @param {MachineAppViewString} pMachineAppView
 * @param {OpenView} pOnClick
 * @returns {LeftPaneItem}
 */
const leftPaneItem = (pStepNumber, pIconElement, pTitle, pMachineAppView, pOnClick) => {
  const lItem = $('<div>')
    .addClass('machineapp_left_pane_item')
    .on('click', pOnClick);

  const lStepBubble = $('<span>')
    .addClass('machineapp_step_bubble')
    .text('STEP ' + pStepNumber);

  const lContent = $('<div>')
    .addClass('machineapp_left_pane_item_content');

  const lIconWrapper = $('<div>')
    .addClass('machineapp_left_pane_item_content_icon_wrapper');

  const lTitle = $('<div>')
    .html(pTitle);

  lItem.append(
    lStepBubble,
    lContent.append(
      lIconWrapper.append(
        pIconElement
      ),
      lTitle
    )
  );

  return {
    widget: lItem,
    controls: {
      updateIcon: pNewIconElement => lIconWrapper.empty().append(pNewIconElement),
      updateTitle: newTitle => lContent.empty().append(lIconWrapper, $('<div>').html(newTitle))
    },
    view: pMachineAppView,
  };
};

/**
 * Icon generating function
 *
 * @param {MachineAppViewString} pMachineAppView
 * @param {MachineAppViewString[]} lItemViews
 * @param {any} machineAppLibrary
 * @returns JQuery | null
 */
const getHeaderIconElement = (pMachineAppView, lItemViews, machineAppLibrary) => {
  const lIsValidated = isStepValid(pMachineAppView, lItemViews, machineAppLibrary);
  if (lIsValidated) {
    return greenCheckbox(true).widget;
  }
  return null;
};

/**
 * @param {MachineAppViewString} view
 * @param {String} pMachineAppType
 * @param {any} machineAppLibrary
 * @returns {String}
 */
const getHeaderTitle = (view, pMachineAppType, machineAppLibrary) => {
  let headerTitle = '';
  switch (view) {
    case gMachineAppViews.machineParameters:
      headerTitle = 'Machine Parameters';
      break;
    case gMachineAppViews.configuration:
      headerTitle = machine_app_implementation_loader.instance.getFlavorText(pMachineAppType).configuration.pluralName;
      break;
    case gMachineAppViews.globalAsset:{
      //TODO: for now use first non default key in globalAssetMap,
      //but we need to consider for later what to do if we have multiple assets to define
      let index = 0;
      const globalAssetFlavorTextMap = Object.values(machine_app_implementation_loader.instance.getFlavorText(pMachineAppType).globalAssetFlavorTextMap);
      if(globalAssetFlavorTextMap.length > 0) {
        index = 1;
      }
      headerTitle = globalAssetFlavorTextMap[index].pluralName;
      break;
    }
    case gMachineAppViews.application:
      headerTitle = machineAppLibrary.listApplications().length === 0 ? 'Operate or Customize' : 'Generated Applications';
      break;
    default:
      console.error(`Unknown view type ${view}`);
      return;
  }
  return headerTitle;
};

/**
 * @param {MachineAppViewString} itemViews
 * @param {String} machineAppType
 * @param {any} machineAppLibrary
 * @param {OpenView} onClick
 * @returns {LeftPaneItem[]}
 */
const getItemHeaders = (itemViews, machineAppType, machineAppLibrary, onClick) => {
  return itemViews.map((view, stepIndex) => leftPaneItem(
    stepIndex + 1,
    getHeaderIconElement(
      view,
      itemViews,
      machineAppLibrary
    ),
    getHeaderTitle(
      view,
      machineAppType,
      machineAppLibrary
    ),
    view,
    () => onClick(view)
  ));
};
